import { useState, useEffect } from 'react';
import { BarChart3, Package, Eye, ShoppingCart, DollarSign, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface BundleStats {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  view_count: number;
  purchase_count: number;
  total_discount_given: number;
  avg_discount: number;
  add_to_cart_count: number;
  stock_limit: number | null;
  stock_sold: number;
}

interface Totals {
  total_bundles: number;
  total_purchases: number;
  total_discount_given: number;
  total_views: number;
}

export default function UpsellBundleAnalytics() {
  const [bundles, setBundles] = useState<BundleStats[]>([]);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  async function fetchAnalytics() {
    try {
      setIsLoading(true);
      const data = await api.get<{ bundles: BundleStats[]; totals: Totals }>('/admin/bundles/analytics');
      setBundles(data.bundles);
      setTotals(data.totals);
    } catch {
      setError('Failed to load analytics.');
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <Link to="/admin/upsell/bundles" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 mb-3">
          <ArrowLeft className="h-4 w-4" />
          Back to Bundles
        </Link>
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">Bundle Analytics</h1>
        </div>
        <p className="text-gray-600 mt-1">Track bundle performance and customer engagement</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Summary cards */}
      {totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg border border-stone-200 p-4">
            <div className="flex items-center gap-2 text-stone-500 text-sm mb-1">
              <Package className="h-4 w-4" aria-hidden="true" />
              Total Bundles
            </div>
            <p className="text-2xl font-bold text-stone-900">{totals.total_bundles}</p>
          </div>
          <div className="bg-white rounded-lg border border-stone-200 p-4">
            <div className="flex items-center gap-2 text-stone-500 text-sm mb-1">
              <Eye className="h-4 w-4" aria-hidden="true" />
              Total Views
            </div>
            <p className="text-2xl font-bold text-stone-900">{totals.total_views.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg border border-stone-200 p-4">
            <div className="flex items-center gap-2 text-stone-500 text-sm mb-1">
              <ShoppingCart className="h-4 w-4" aria-hidden="true" />
              Total Purchases
            </div>
            <p className="text-2xl font-bold text-stone-900">{totals.total_purchases}</p>
          </div>
          <div className="bg-white rounded-lg border border-stone-200 p-4">
            <div className="flex items-center gap-2 text-stone-500 text-sm mb-1">
              <DollarSign className="h-4 w-4" aria-hidden="true" />
              Total Discounts Given
            </div>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(totals.total_discount_given)}</p>
          </div>
        </div>
      )}

      {/* Per-bundle table */}
      {bundles.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <BarChart3 className="w-12 h-12 text-stone-300 mx-auto mb-3" aria-hidden="true" />
            <p className="text-stone-500">No bundle data yet. Create bundles and start selling to see analytics.</p>
          </div>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-stone-700">Bundle</th>
                  <th className="text-right py-3 px-4 font-medium text-stone-700">Views</th>
                  <th className="text-right py-3 px-4 font-medium text-stone-700">Add to Cart</th>
                  <th className="text-right py-3 px-4 font-medium text-stone-700">Purchases</th>
                  <th className="text-right py-3 px-4 font-medium text-stone-700">Conversion</th>
                  <th className="text-right py-3 px-4 font-medium text-stone-700">Avg Discount</th>
                  <th className="text-right py-3 px-4 font-medium text-stone-700">Total Discounts</th>
                </tr>
              </thead>
              <tbody>
                {bundles.map(bundle => {
                  const conversionRate = bundle.view_count > 0
                    ? ((bundle.purchase_count / bundle.view_count) * 100).toFixed(1)
                    : '0.0';
                  return (
                    <tr key={bundle.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Link to={`/admin/upsell/bundles/${bundle.id}/edit`} className="font-medium text-stone-900 hover:text-green-700">
                            {bundle.name}
                          </Link>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${bundle.is_active ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-500'}`}>
                            {bundle.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right text-stone-600">{bundle.view_count.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right text-stone-600">{bundle.add_to_cart_count}</td>
                      <td className="py-3 px-4 text-right font-medium text-stone-900">{bundle.purchase_count}</td>
                      <td className="py-3 px-4 text-right text-stone-600">{conversionRate}%</td>
                      <td className="py-3 px-4 text-right text-stone-600">{formatCurrency(Number(bundle.avg_discount))}</td>
                      <td className="py-3 px-4 text-right font-medium text-green-700">{formatCurrency(Number(bundle.total_discount_given))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
